import { z } from 'zod';

const FunnelKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z0-9]+(_[a-z0-9]+)*$/, 'key must be snake_case');

const EntryPathSchema = z
  .string()
  .trim()
  .regex(
    /^\/[a-z0-9]+(?:[-/][a-z0-9]+)*$/,
    'entryPath must be a lowercase path',
  );

export const FunnelQuestionTypeSchema = z.enum([
  'single_select',
  'multi_select',
  'text',
  'text_pair',
]);

export const SUBJECT_TOKEN = '{subject}';

const CmsTextSchema = z
  .string()
  .nullish()
  .transform((value) => {
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? undefined : trimmed;
  });

const CmsCountSchema = z
  .number()
  .int()
  .positive()
  .nullish()
  .transform((value) => value ?? undefined);

const CmsFlagSchema = z
  .boolean()
  .nullish()
  .transform((value) => value ?? false);

const CmsKeySchema = FunnelKeySchema.nullish().transform(
  (value) => value ?? undefined,
);

const CmsSettingsSchema = z
  .record(z.unknown())
  .nullish()
  .transform((value) => value ?? undefined)
  .catch(undefined);

const CmsPronounTitlesSchema = z
  .object({ id: z.number().optional() })
  .catchall(z.string().nullish())
  .nullish()
  .transform((value) => {
    if (value === null || value === undefined) {
      return undefined;
    }

    const variants = Object.entries(value).flatMap(([key, title]) =>
      key === 'id' || typeof title !== 'string' || title.trim() === ''
        ? []
        : [[key, title.trim()] as const],
    );

    return variants.length === 0
      ? undefined
      : (Object.fromEntries(variants) as PronounTitles);
  });

const CmsMediaSchema = z
  .union([z.string(), z.object({ url: z.string() }).passthrough()])
  .nullish()
  .transform((value) => {
    if (value === null || value === undefined) {
      return undefined;
    }

    const url = typeof value === 'string' ? value : value.url;
    return url.trim() === '' ? undefined : url;
  });

export const CmsFunnelAnswerSchema = z.object({
  answerKey: FunnelKeySchema,
  label: CmsTextSchema,
  sublabel: CmsTextSchema,
  placeholder: CmsTextSchema,
  icon: CmsMediaSchema,
  opensTextInput: CmsFlagSchema,
  pronounSet: CmsKeySchema.transform((value) =>
    value === 'none' ? undefined : value,
  ),
});

const TEXT_FIELD_COUNT = 1;
const TEXT_PAIR_FIELD_COUNT = 2;

const FIELD_COUNT_BY_TYPE: Partial<Record<FunnelQuestionType, number>> = {
  text: TEXT_FIELD_COUNT,
  text_pair: TEXT_PAIR_FIELD_COUNT,
};

export const SELECT_QUESTION_TYPES: readonly FunnelQuestionType[] = [
  'single_select',
  'multi_select',
];

export function isSelectQuestionType(type: FunnelQuestionType): boolean {
  return SELECT_QUESTION_TYPES.includes(type);
}

export const CmsFunnelQuestionSchema = z
  .object({
    questionKey: FunnelKeySchema,
    type: FunnelQuestionTypeSchema,
    defaultTitle: z.string().min(1),
    defaultSubtitle: CmsTextSchema,
    fallbackTitle: CmsTextSchema,
    pronounTitles: CmsPronounTitlesSchema,
    subjectMaxLength: CmsCountSchema,
    infoText: CmsTextSchema,
    inputMaxLength: CmsCountSchema,
    answers: z.array(CmsFunnelAnswerSchema).min(1),
  })
  .superRefine((question, ctx) => {
    const seenAnswerKeys = new Set<string>();

    for (const answer of question.answers) {
      if (seenAnswerKeys.has(answer.answerKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['answers'],
          message: `duplicate answerKey "${answer.answerKey}"`,
        });
      }

      seenAnswerKeys.add(answer.answerKey);
    }

    const fieldCount = FIELD_COUNT_BY_TYPE[question.type];

    if (fieldCount !== undefined && question.answers.length !== fieldCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['answers'],
        message: `"${question.type}" questions must define exactly ${fieldCount} field(s)`,
      });
    }

    const otherAnswers = question.answers.filter(
      (answer) => answer.opensTextInput,
    );

    if (otherAnswers.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['answers'],
        message: 'only one answer may open a text input',
      });
    }

    if (!isSelectQuestionType(question.type)) {
      if (otherAnswers.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['answers'],
          message: `opensTextInput is not allowed for type "${question.type}"`,
        });
      }

      return;
    }

    for (const answer of question.answers) {
      if (answer.label === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['answers', answer.answerKey],
          message: 'label is required for select answers',
        });
      }
    }
  });

export const CmsFunnelStepSchema = z.object({
  stepKey: FunnelKeySchema,
  order: z.number().int(),
  required: CmsFlagSchema,
  skippable: CmsFlagSchema,
  typeOverride: FunnelQuestionTypeSchema.nullish().transform(
    (value) => value ?? undefined,
  ),
  titleOverride: CmsTextSchema,
  subtitleOverride: CmsTextSchema,
  infoTitle: CmsTextSchema,
  infoText: CmsTextSchema,
  infoTextOverride: CmsTextSchema,
  inputMaxLengthOverride: CmsCountSchema,
  subjectMaxLengthOverride: CmsCountSchema,
  settings: CmsSettingsSchema,
  question: CmsFunnelQuestionSchema,
});

export const CmsFunnelBaseSchema = z.object({
  funnelKey: FunnelKeySchema,
  funnelName: z.string().min(1),
  isActive: z.boolean(),
  entryPath: EntryPathSchema,
  version: z
    .number()
    .int()
    .nullish()
    .transform((value) => value ?? undefined),
  steps: z.array(z.unknown()).default([]),
});

export type PronounTitles = Record<string, string>;
export type FunnelQuestionType = z.infer<typeof FunnelQuestionTypeSchema>;
export type CmsFunnelAnswer = z.infer<typeof CmsFunnelAnswerSchema>;
export type CmsFunnelQuestion = z.infer<typeof CmsFunnelQuestionSchema>;
export type CmsFunnelStep = z.infer<typeof CmsFunnelStepSchema>;

export interface CmsFunnel extends Omit<
  z.infer<typeof CmsFunnelBaseSchema>,
  'steps'
> {
  steps: CmsFunnelStep[];
}
